<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick, computed } from 'vue';

const IS_SSR = typeof window === 'undefined';

const props = withDefaults(
    defineProps<{
        items: unknown[];
        gap?: number; // px gap between items
        align?: 'start' | 'center' | 'end' | 'stretch';
        minWidth?: number; // px minimum width per card (decides mode)
        showArrows?: boolean;
        forceMode?: 'row' | 'carousel' | null;
    }>(),
    {
        gap: 12,
        align: 'center',
        minWidth: 280,
        showArrows: true,
        forceMode: null,
    }
);

const rootEl = ref<HTMLElement | null>(null);
const scrollerEl = ref<HTMLElement | null>(null);

// During SSR & hydration, keep DOM shape stable.
// Only switch to triple-band carousel after hydration.
const hydrated = ref(false);
const isCarousel = ref(false);

const canPrev = ref(false);
const canNext = ref(false);

let ro: ResizeObserver | null = null;
let isNormalizing = false;

const alignClass = computed(() => {
    switch (props.align) {
        case 'start':
            return 'items-start';
        case 'end':
            return 'items-end';
        case 'stretch':
            return 'items-stretch';
        default:
            return 'items-center';
    }
});

/**
 * During hydration, always render 1× to match SSR HTML.
 * After hydration, if in carousel, switch to 3× for seamless loop.
 */
const renderItems = computed(() => {
    const base = props.items ?? [];
    if (!hydrated.value) return base;
    if (isCarousel.value) return [...base, ...base, ...base];
    return base;
});

/** Logical width (width of one band of items) */
function logicalWidth(): number {
    const n = props.items.length;
    if (n <= 0) return 0;
    return n * (props.minWidth + props.gap) - props.gap;
}

/** Required width if all items were shown in a row */
function requiredWidth(): number {
    const n = props.items.length;
    if (n <= 0) return 0;
    return n * props.minWidth + (n - 1) * props.gap;
}

/** Scroll step per click / arrow */
function stepWidth(): number {
    return props.minWidth + props.gap;
}

function centerOnMiddleBand() {
    const el = scrollerEl.value;
    if (!el) return;
    const w = logicalWidth();
    if (w > 0) {
        isNormalizing = true;
        el.scrollLeft = w; // band #2
        isNormalizing = false;
    }
}

function normalizeDuringScroll() {
    const el = scrollerEl.value;
    if (!el || !isCarousel.value || !hydrated.value || isNormalizing) return;
    const w = logicalWidth();
    if (w <= 0) return;

    const sl = el.scrollLeft;
    if (sl < w || sl >= 2 * w) {
        isNormalizing = true;
        const offset = (((sl - w) % w) + w) % w;
        el.scrollLeft = w + offset;
        isNormalizing = false;
    }
    updateScrollState();
}

function updateScrollState() {
    const el = scrollerEl.value;
    if (!el) {
        canPrev.value = false;
        canNext.value = false;
        return;
    }
    if (isCarousel.value) {
        // infinite — arrows always enabled
        canPrev.value = true;
        canNext.value = true;
    } else {
        const sl = Math.round(el.scrollLeft);
        const max = Math.max(0, Math.round(el.scrollWidth - el.clientWidth));
        canPrev.value = sl > 0;
        canNext.value = sl < max;
    }
}

/**
 * Decide mode.
 * On the server: stick to row (or honor forceMode).
 * On the client post-hydration: measure & possibly flip to carousel.
 */
async function decideModeAfterHydration() {
    if (IS_SSR) return;

    await nextTick();

    if (props.forceMode === 'row') {
        isCarousel.value = false;
    } else if (props.forceMode === 'carousel') {
        isCarousel.value = true;
    } else {
        const el = scrollerEl.value;
        if (!el) {
            isCarousel.value = false;
        } else {
            const needsCarousel = requiredWidth() > el.clientWidth + 1;
            isCarousel.value = needsCarousel;
        }
    }

    if (isCarousel.value) centerOnMiddleBand();
    updateScrollState();
}

function scrollByOne(dir: 1 | -1) {
    const el = scrollerEl.value;
    if (!el) return;
    el.scrollBy({ left: stepWidth() * dir, behavior: 'smooth' });
    window.setTimeout(() => {
        if (isCarousel.value) normalizeDuringScroll();
        else updateScrollState();
    }, 300);
}

function onScroll() {
    if (isCarousel.value) normalizeDuringScroll();
    else updateScrollState();
}

function attachBrowserHandlers() {
    if (IS_SSR) return;

    const RO = (window as any).ResizeObserver;
    if (RO) {
        ro = new RO(() => {
            decideModeAfterHydration();
        });
        if (rootEl.value && ro !== null) ro.observe(rootEl.value);
        if (scrollerEl.value && ro !== null) ro.observe(scrollerEl.value);
    }

    window.addEventListener('resize', decideModeAfterHydration);
    scrollerEl.value?.addEventListener('scroll', onScroll, { passive: true });
}

function detachBrowserHandlers() {
    if (IS_SSR) return;
    ro?.disconnect();
    ro = null;
    window.removeEventListener('resize', decideModeAfterHydration);
    scrollerEl.value?.removeEventListener('scroll', onScroll);
}

/** --- Lifecycle --- */
if (!IS_SSR) {
    onMounted(async () => {
        hydrated.value = true;

        attachBrowserHandlers();

        await nextTick();
        requestAnimationFrame(decideModeAfterHydration);
    });

    onBeforeUnmount(() => {
        detachBrowserHandlers();
    });
}
</script>

<template>
    <section ref="rootEl" class="relative w-full">
        <div
            ref="scrollerEl"
            class="no-scrollbar w-full overflow-x-auto"
            :class="hydrated && isCarousel ? 'whitespace-nowrap' : ''"
        >
            <div
                class="inline-flex w-auto p-2"
                :class="[alignClass, hydrated && isCarousel ? 'flex-nowrap' : 'flex-wrap']"
                :style="{
                    columnGap: `${gap}px`,
                    rowGap: hydrated && isCarousel ? undefined : `${gap}px`,
                }"
            >
                <div
                    v-for="(item, i) in renderItems"
                    :key="i"
                    class="inline-flex min-w-0"
                    :style="{
                        minWidth: `${minWidth}px`,
                        width: hydrated && isCarousel ? `${minWidth}px` : undefined,
                        flex: hydrated && isCarousel ? '0 0 auto' : '1 1 0',
                    }"
                >
                    <!-- Provide item + logicalIndex (mod original items length) -->
                    <slot name="item" :item="item" :index="i % (items.length || 1)" />
                </div>
            </div>
        </div>

        <!-- Arrows (client-only carousel) -->
        <button
            v-if="hydrated && isCarousel && showArrows"
            type="button"
            aria-label="Previous"
            class="absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-black/40 text-white backdrop-blur px-2 py-2 ring-1 ring-white/20 hover:bg-black/55 disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="!canPrev"
            @click="scrollByOne(-1)"
        >
            ‹
        </button>

        <button
            v-if="hydrated && isCarousel && showArrows"
            type="button"
            aria-label="Next"
            class="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-black/40 text-white backdrop-blur px-2 py-2 ring-1 ring-white/20 hover:bg-black/55 disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="!canNext"
            @click="scrollByOne(1)"
        >
            ›
        </button>
    </section>
</template>

<style scoped>
.no-scrollbar {
    scrollbar-width: none;
}
.no-scrollbar::-webkit-scrollbar {
    display: none;
}
</style>
